/**
 * useScoreFeed — Subscribes to the backend SSE stream and emits
 * `score_update` events filtered to a given set of entity IDs.
 *
 * Returns an object: `{ floaters, status, eventsTotal, lastEventAt }`.
 *   floaters     — array of `{ id, entity_id, points, source, event_type, ts }`.
 *                  Each item lives for ~3.2s then auto-removes itself.
 *   status       — 'connecting' | 'live' | 'reconnecting' | 'closed'
 *                  Drives the on-screen LIVE pulse indicator.
 *   eventsTotal  — running count of `score_update` payloads received.
 *   lastEventAt  — epoch ms of the most recent event (null if none yet).
 *
 * The hook owns a SINGLE EventSource per mount; multiple consumers should
 * call this hook at the parent level and pass the array down.
 *
 * Auth: HttpOnly cookie travels automatically (`withCredentials: true`).
 */
import { useEffect, useRef, useState } from 'react';

// v2026-06 (admin-tunable Stage FX + jackpot) — the floater cadence, lifetimes,
// on-screen cap AND jackpot behaviour are ADMIN-TUNABLE live (Economy panel →
// "Stage Live Feed") and served by GET /api/config/stage-fx. The constants in
// FX_DEFAULTS below are only the fallback used until that config resolves (or
// if it fails).
const FX_DEFAULTS = {
  scatterMs: 5000,         // uniform-random on-screen scatter per arriving floater (≈ flusher tick)
  jackpotEnabled: true,
  jackpotMinValue: 500,    // ₣ threshold for a high-value event
  jackpotFlurryCount: 14,  // celebratory floaters sprayed across the board
  jackpotFlurryMs: 1400,   // flurry duration
  floaterTtlMs: 3200,      // how long a scored-event floater lingers (ms)
  mentionTtlMs: 2200,      // 🐦 mentions fade faster so a heavy stream can't pile up
  maxFloaters: 80,         // hard cap on concurrent on-screen floaters
};
const META_FLUSH_MS = 1000;
// Module-level cache so every Stage mount shares ONE config fetch.
let _fxCache = null;
let _fxFetch = null;
const loadStageFx = () => {
  if (_fxCache) return Promise.resolve(_fxCache);
  if (_fxFetch) return _fxFetch;
  const base = process.env.REACT_APP_BACKEND_URL;
  _fxFetch = fetch(`${base}/api/config/stage-fx`, { credentials: 'include' })
    .then((r) => (r.ok ? r.json() : null))
    .then((j) => { _fxCache = { ...FX_DEFAULTS, ...(j || {}) }; return _fxCache; })
    .catch(() => { _fxCache = { ...FX_DEFAULTS }; return _fxCache; });
  return _fxFetch;
};
export default function useScoreFeed(entityIdSet) {
  const [floaters, setFloaters] = useState([]);
  const [status, setStatus] = useState('connecting');
  const [eventsTotal, setEventsTotal] = useState(0);
  const [lastEventAt, setLastEventAt] = useState(null);
  const sourceRef = useRef(null);
  // 2026-07-21 audit — Set (not array) so fired timers self-prune; a long
  // Stage session under a heavy stream no longer accumulates stale handles.
  const timeoutsRef = useRef(new Set());
  // Throttled meta counters — incremented in the SSE handler, flushed
  // to state on a setInterval. Avoid re-renders for every irrelevant tick.
  const pendingTotalRef = useRef(0);
  const pendingLastAtRef = useRef(null);
  // v2026-06 (admin-tunable Stage FX + jackpot) — live scatter/jackpot config.
  const fxRef = useRef(_fxCache || FX_DEFAULTS);
  useEffect(() => {
    let on = true;
    loadStageFx().then((fx) => { if (on) fxRef.current = fx; });
    return () => { on = false; };
  }, []);

  // AUDIT P1 (2026-06 full sweep) — the SSE subscription effect must NOT key
  // off the Set's object identity. A consumer that builds `new Set(...)` on
  // every render would otherwise tear down + recreate the EventSource each
  // render (reconnect storm / duplicate streams). We derive a STABLE content
  // key (sorted ids) for the effect dependency and read the latest Set via a
  // ref inside the message handler, so the connection only re-establishes when
  // the owned-entity SET actually changes — regardless of identity churn.
  const entityKey = entityIdSet && entityIdSet.size
    ? Array.from(entityIdSet).sort().join(',')
    : '';
  const entityIdSetRef = useRef(entityIdSet);
  entityIdSetRef.current = entityIdSet;

  useEffect(() => {
    if (!entityKey) {
      setStatus('closed');
      return undefined;
    }

    const base = process.env.REACT_APP_BACKEND_URL;
    if (!base) {
      setStatus('closed');
      return undefined;
    }
    const url = `${base}/api/events/stream`;

    let es;
    try {
      setStatus('connecting');
      es = new EventSource(url, { withCredentials: true });
      sourceRef.current = es;
    } catch (e) {
      console.warn('[useScoreFeed] EventSource failed:', e);
      setStatus('closed');
      return undefined;
    }

    es.onopen = () => setStatus('live');
    es.onerror = () => {
      // EventSource auto-reconnects; surface that state to the indicator
      setStatus(es.readyState === EventSource.CLOSED ? 'closed' : 'reconnecting');
    };

    // ── Random-scatter floater release (2026-07-21 owner request) ─────
    // The backend schedules every event/🐦 at a RANDOM time across its
    // probe window (60-min trickle/news buffer), but the flusher delivers
    // due docs in ~5s ticks — a batch would otherwise pop at one instant.
    // Each floater is re-scattered at an independent uniform-random delay
    // within `scatterMs` (≈ the flusher tick), reconstructing the
    // backend's continuous random timeline on screen. Sub-second
    // collisions are chance-only — more likely for very high-mention
    // people, by design.
    const showFloater = (f) => {
      const fx = fxRef.current;
      setFloaters((prev) => {
        const trimmed = prev.length >= fx.maxFloaters
          ? prev.slice(prev.length - fx.maxFloaters + 1)
          : prev;
        return [...trimmed, f];
      });
      const t = setTimeout(() => {
        timeoutsRef.current.delete(t);
        setFloaters((prev) => prev.filter((x) => x.id !== f.id));
      }, f.isMention ? fx.mentionTtlMs : fx.floaterTtlMs);
      timeoutsRef.current.add(t);
    };
    // Release a floater at an independent random moment within `spreadMs`.
    const scatterShow = (f, spreadMs) => {
      const t = setTimeout(() => {
        timeoutsRef.current.delete(t);
        showFloater(f);
      }, Math.random() * Math.max(0, spreadMs || 0));
      timeoutsRef.current.add(t);
    };
    // Build 4 — 🐦 mention pacing. A reconnect/backlog burst used to dump
    // dozens of bird floaters into one ~5s scatter window (owner-reported
    // "early flurry"). Each pending mention now adds a fixed gap so bursts
    // spread out over time; sporadic singles still show near-instantly.
    // Overflow beyond the cap is dropped — mentions are ambience, not scores.
    const MENTION_GAP_MS = 900;
    const MENTION_MAX_PENDING = 24;
    let mentionBacklog = 0;
    const paceMention = (f) => {
      if (mentionBacklog >= MENTION_MAX_PENDING) return;
      const fx = fxRef.current;
      const delay = Math.random() * Math.max(0, fx.scatterMs || 0) + mentionBacklog * MENTION_GAP_MS;
      mentionBacklog += 1;
      const t = setTimeout(() => {
        timeoutsRef.current.delete(t);
        mentionBacklog = Math.max(0, mentionBacklog - 1);
        showFloater(f);
      }, delay);
      timeoutsRef.current.add(t);
    };
    // Spawn a celebratory flurry across random OWNED slots when a high-value
    // event lands → the "jackpot" moment. Floaters are icon-only (mention
    // style) so they read as a burst of buzz across the board, not extra ₣ —
    // each fires at a random moment within the flurry window.
    const spawnJackpotFlurry = (triggerEmoji) => {
      const owned = entityIdSetRef.current ? Array.from(entityIdSetRef.current) : [];
      if (owned.length === 0) return;
      const fx = fxRef.current;
      for (let i = 0; i < fx.jackpotFlurryCount; i += 1) {
        const eid = owned[Math.floor(Math.random() * owned.length)];
        scatterShow({
          id: `jp_${eid}_${Date.now()}_${i}_${Math.random().toString(36).slice(2, 6)}`,
          entity_id: eid,
          points: 0,
          source: 'jackpot',
          event_type: 'jackpot',
          emoji: triggerEmoji || '✨',
          visual: { kind: 'mention', direction: 'up', icon: triggerEmoji || '✨' },
          isMention: true,
          jackpot: true,
          ts: Date.now(),
        }, fx.jackpotFlurryMs);
      }
    };

    // Flush coalesced meta-counters at most once per second.
    const flushInterval = setInterval(() => {
      const delta = pendingTotalRef.current;
      const lastAt = pendingLastAtRef.current;
      if (delta > 0) {
        pendingTotalRef.current = 0;
        setEventsTotal((n) => n + delta);
        if (lastAt) setLastEventAt(lastAt);
      }
    }, META_FLUSH_MS);

    // Backend wraps payloads as `data: {"event":"score_update","data":{...}}`
    // without an SSE `event:` line, so all messages arrive on `onmessage`.
    es.onmessage = (evt) => {
      let envelope;
      try { envelope = JSON.parse(evt.data); } catch { return; }
      if (envelope?.event === 'connected') return;  // initial handshake
      if (envelope?.event !== 'score_update' || !envelope?.data) return;
      const data = envelope.data;
      // Coalesce meta into refs — flushed every META_FLUSH_MS.
      pendingTotalRef.current += 1;
      pendingLastAtRef.current = Date.now();
      // Only commit a floater when the event affects an entity the user owns.
      // Read the latest owned-entity set via the ref so the subscription
      // doesn't need to re-establish when only the set CONTENTS change.
      if (!entityIdSetRef.current || !entityIdSetRef.current.has(data.entity_id)) return;
      const id = `sf_${data.entity_id}_${data.timestamp}_${Math.random().toString(36).slice(2, 7)}`;
      // P3 — pass the additive backend `visual` block through (kind / direction
      // / show_number / magnitude / icon). mentions (🐦) drift LEFT icon-only;
      // real events drift RIGHT with their emoji + value.
      const visual = data.visual || null;
      const isMention = (visual?.kind === 'mention')
        || data.source === 'x_mention' || data.source === 'x_post'
        || data.event_type === 'x_at_mention';
      const floater = {
        id,
        entity_id: data.entity_id,
        points: Number(data.points) || 0,
        source: data.source || 'news',
        event_type: data.event_type || 'event',
        // v5.7.54 — backend now supplies the event-type emoji via the
        // single `scoring_engine.EVENT_EMOJIS` map. Pass it through so
        // ScoreFloater renders the correct symbol (🕊️ peace deal,
        // ⚽ goal, 🏆 award_win, …) without keeping a parallel
        // frontend map that drifts out of sync.
        emoji: data.emoji || null,
        visual,
        isMention,
        ts: Date.now(),
      };
      // v2026-06 — a high-value event triggers a jackpot flurry across the board.
      if (!isMention && fxRef.current.jackpotEnabled
          && Math.abs(Number(data.points) || 0) >= fxRef.current.jackpotMinValue) {
        spawnJackpotFlurry(floater.emoji);
      }
      // 2026-07-21 (owner request) — independent uniform-random release
      // within the scatter window (replaces the pooled Pareto drain).
      // Build 4 — mentions go through the pacing queue instead (rate-limited).
      if (floater.isMention) paceMention(floater);
      else scatterShow(floater, fxRef.current.scatterMs);
    };

    return () => {
      es.onmessage = null;
      es.onerror = null;
      es.onopen = null;
      es.close();
      sourceRef.current = null;
      clearInterval(flushInterval);
      timeoutsRef.current.forEach(clearTimeout);
      timeoutsRef.current = new Set();
      setStatus('closed');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entityKey]);

  return { floaters, status, eventsTotal, lastEventAt };
}
